import { env } from '@/lib/env';
import { tokenStore } from '@/lib/token-store';
import type { components } from '@geefamily/api';

export type UserProfile = components['schemas']['UserProfileResponse'];
export type AuthTokens = components['schemas']['AuthTokensResponse'];

/**
 * The refresh currently in flight, if any.
 *
 * Refreshing is NOT idempotent — it rotates the token — so two callers must
 * never both do it. There are three independent ones: the typed client's 401
 * handler, the file uploader's, and AuthProvider's startup probe. Each used to
 * serialise only against itself, which left the interesting case unguarded:
 * several queries 401 together the moment an access token expires, and that is
 * precisely when two of them race.
 *
 * The loser of that race is not harmless. It either collides on the session
 * row's version and gets a 500, or — worse — arrives with a token the winner has
 * already consumed, which the API correctly reads as a REPLAYED token and
 * answers by revoking every session the member has.
 *
 * One promise, shared by everyone, removes the race rather than handling it.
 */
let refreshInFlight: Promise<AuthTokens | null> | null = null;

/**
 * Refreshes the session using the HttpOnly cookie the API set at sign-in.
 *
 * Written with bare `fetch` rather than the typed client on purpose: the client
 * calls this from its own 401 handler, and routing it back through the client
 * would recurse the first time a refresh itself returned 401.
 */
export function refreshSession(): Promise<AuthTokens | null> {
  refreshInFlight ??= runRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

async function runRefresh(): Promise<AuthTokens | null> {
  try {
    const response = await fetch(`${env.apiBaseUrl}/api/v1/identity/auth/refresh`, {
      method: 'POST',
      // Sends the gf_refresh cookie. Without this the request is anonymous and
      // the refresh always fails.
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });

    if (!response.ok) {
      tokenStore.clear();
      return null;
    }

    const tokens = (await response.json()) as AuthTokens;
    tokenStore.set(tokens.accessToken);

    return tokens;
  } catch {
    // Offline, or the API is down. Indistinguishable from "no session" here, and
    // the caller treats both as signed out.
    tokenStore.clear();
    return null;
  }
}

export async function endSession(): Promise<void> {
  try {
    await fetch(`${env.apiBaseUrl}/api/v1/identity/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
  } catch {
    // Even if the server never hears about it, the local session ends.
  } finally {
    tokenStore.clear();
  }
}
